import { useState } from 'react'
import type { ReactNode } from 'react'
import {
  ApiError, commissionNode, commissionRecorder,
  type CommissionOut, type RecorderOut,
} from '../api'
import { invalidate } from '../useApi'
import { Link } from '../router'
import { PageHead } from '../components/Shell'
import { Field } from '../components/bits'
import { LocalityPicker, type Place } from '../components/LocalityPicker'

type Kind = 'node' | 'recorder'

/** Models we know how to read a card from, plus whatever the operator types.
 *  A datalist, not a select: the value picks the filename and header parser at
 *  import time so consistent spelling matters, but a unit nobody has written a
 *  parser for still has to be registrable the day it arrives. */
const KNOWN_MODELS = [
  'HydroMoth 1.0', 'HydroMoth 1.2', 'AudioMoth 1.2',
  'SoundTrap ST300', 'SoundTrap ST600',
  'Wildlife Acoustics SM4', 'RTSys RESEA',
]

/** Recorders calibrated end to end, with no hydrophone terminal in the middle.
 *
 *  A HydroMoth is a sealed board: MEMS element, gain stage and ADC are one
 *  part, and there is nowhere on it where a voltage per µPa exists to be
 *  measured. What gets published for these units is a single figure for the
 *  whole chain — what sound pressure produces digital full scale, at a stated
 *  gain step and sample rate. A SoundTrap ships the same shape of number per
 *  serial. Neither is a `dBV re 1 µPa` sensitivity, and the two differ by the
 *  full-scale voltage, so a figure copied from one into the other is wrong by
 *  however many dB that happens to be — an error that reads as a plausible
 *  number rather than as a mistake.
 *
 *  Matched loosely, on purpose. The field is free text so the exact spelling
 *  varies, and the cost of the two directions is not symmetric: a model that
 *  slips past this is asked a question it can leave empty, while one wrongly
 *  caught here loses a field it needed. Anything unrecognised is asked.
 */
const END_TO_END = /audiomoth|hydromoth|soundtrap/i

/** Gain as devices state it. Not dB, and deliberately not converted: what a
 *  step means depends on board revision and firmware, so a looked-up number
 *  would let the server report a level nobody measured. */
const GAIN_STEPS = ['low', 'low-medium', 'medium', 'medium-high', 'high']

interface Step {
  id: string
  title: string
  /** The one question this step asks. Shown under the title. */
  lead?: string
  body: ReactNode
  /** False while the step's own question is unanswered. Next is disabled
   *  rather than hidden, so the form does not appear to have ended. */
  ready: boolean
}

/** Add hardware, one decision at a time.
 *
 *  This replaced two single-page forms reached from two buttons. The forms
 *  asked twelve questions at once, most of which only matter for one of the
 *  two kinds of hardware, and the choice between them was made by which button
 *  you pressed — before anything explained the difference. Now the difference
 *  is the first question, and each step after it asks for one thing.
 */
export function Add() {
  const [step, setStep] = useState(0)
  const [kind, setKind] = useState<Kind | null>(null)
  const [place, setPlace] = useState<Place | null>(null)

  const [serial, setSerial] = useState('')
  const [model, setModel] = useState('')
  const [label, setLabel] = useState('')
  const [micType, setMicType] = useState('')

  const [sensitivity, setSensitivity] = useState('')
  const [certRef, setCertRef] = useState('')

  const [depth, setDepth] = useState('')
  const [gain, setGain] = useState('')
  const [tier, setTier] = useState('events')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [notes, setNotes] = useState('')
  // Defaults to a real deployment because commissioning hardware is a
  // deliberate act -- but asked, because a mission mislabelled here is a
  // living room counted as ocean.
  const [purpose, setPurpose] = useState<'deployment' | 'test'>('deployment')

  // Whether the hydrophone in this unit is a separable thing with a
  // sensitivity of its own. Read from the model rather than asked, because it
  // is a property of the hardware and the operator holding the unit should not
  // have to know which side of that line it falls on.
  const integrated = END_TO_END.test(model)

  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [doneNode, setDoneNode] = useState<CommissionOut | null>(null)
  const [doneRec, setDoneRec] = useState<RecorderOut | null>(null)

  // datetime-local yields wall clock with no zone. Read through the browser's
  // own offset: a silent UTC assumption is wrong by an hour for half the year
  // here, and this window later decides which recordings are attributable.
  const asInstant = (local: string) => new Date(local).toISOString()
  const windowBad = !!from && !!to && new Date(to) <= new Date(from)

  const placeStep: Step = {
    id: 'where',
    title: 'Where is it going?',
    lead: 'Most deployments are at a licensed farm, which already has a number and a position.',
    body: <LocalityPicker value={place} onChange={setPlace} />,
    ready: !!place?.name,
  }

  const nodeSteps: Step[] = [
    placeStep,
    {
      id: 'serial',
      title: 'Hydrophone serial',
      lead: 'From the label on the hydrophone itself, not the Pi.',
      body: (
        <Field label="Serial">
          <input value={serial} autoFocus onChange={(e) => setSerial(e.target.value)}
            placeholder="3260201" />
        </Field>
      ),
      ready: !!serial.trim(),
    },
    {
      id: 'model',
      title: 'Which hydrophone is it?',
      lead: 'Optional, but it is what tells you later what you were listening with.',
      body: (
        <Field label="Model">
          <input value={micType} autoFocus onChange={(e) => setMicType(e.target.value)}
            placeholder="A5iM15" />
        </Field>
      ),
      ready: true,
    },
    {
      id: 'purpose',
      title: 'Is this going in the sea?',
      lead: 'A bench or desk test records real audio and is otherwise indistinguishable from a deployment. Only what goes in the water counts towards Ocean impact.',
      body: (
        <div className="stack" style={{ gap: 10 }}>
          <button type="button" className={`pick${purpose === 'deployment' ? ' on' : ''}`}
            onClick={() => setPurpose('deployment')}>
            <strong>In the water</strong>
            <span>A real deployment. Its recordings count towards what Phoniq reports about the ocean.</span>
          </button>
          <button type="button" className={`pick${purpose === 'test' ? ' on' : ''}`}
            onClick={() => setPurpose('test')}>
            <strong>Test or bench</strong>
            <span>Kept and listenable like any other mission, but excluded from Ocean impact.</span>
          </button>
        </div>
      ),
      ready: true,
    },
    {
      id: 'cal',
      title: 'Do you have a calibration certificate?',
      lead: 'Leave empty if not. Levels stay site-relative until a measured sensitivity exists, which is the honest state — not a limitation to work around.',
      body: (
        <>
          <Field label="Sensitivity (dBV re 1 µPa)">
            <input type="number" step="0.1" value={sensitivity} autoFocus
              onChange={(e) => setSensitivity(e.target.value)} placeholder="-180.0" />
          </Field>
          {sensitivity !== '' && (
            <Field label="Certificate reference" hint="Where that figure came from, so it is auditable later.">
              <input value={certRef} onChange={(e) => setCertRef(e.target.value)}
                placeholder="cert-2026-0142 or a URL" />
            </Field>
          )}
        </>
      ),
      ready: true,
    },
    {
      id: 'deploy',
      title: 'Depth and tier',
      body: (
        <>
          <Field label="Deploy depth (m)">
            <input type="number" step="0.1" value={depth} autoFocus
              onChange={(e) => setDepth(e.target.value)} />
          </Field>
          <Field label="Tier" hint="Which kinds of data the node may send. 'events' is the fleet default.">
            <select value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="metrics">metrics — roll-ups only</option>
              <option value="events">events — spectra, clips, live</option>
              <option value="reduced">reduced — plus reduced audio</option>
              <option value="full">full — plus continuous audio</option>
            </select>
          </Field>
        </>
      ),
      ready: true,
    },
  ]

  const recorderSteps: Step[] = [
    placeStep,
    {
      id: 'serial',
      title: 'Serial on the unit',
      lead: 'This is what ties a memory card back to the hardware. Re-deploying the same serial keeps its calibration history.',
      body: (
        <Field label="Serial">
          <input value={serial} autoFocus onChange={(e) => setSerial(e.target.value)}
            placeholder="247A3F1C0B" />
        </Field>
      ),
      ready: !!serial.trim(),
    },
    {
      id: 'model',
      title: 'Make and model',
      lead: 'This picks how the card’s filenames and file headers are read when you import it. Type anything — an unknown model still registers and falls back to reading timestamps generically.',
      body: (
        <>
          <Field label="Model">
            <input list="add-models" value={model} autoFocus
              onChange={(e) => setModel(e.target.value)} placeholder="HydroMoth 1.0" />
            <datalist id="add-models">
              {KNOWN_MODELS.map((m) => <option key={m} value={m} />)}
            </datalist>
          </Field>
          <Field label="Label" hint="What the team calls it. Technicians read labels, not identifiers.">
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="HydroMoth 3" />
          </Field>
        </>
      ),
      ready: !!model.trim(),
    },
    {
      id: 'window',
      title: 'When was it in the water?',
      lead: 'Not now — when it actually went in. Recordings outside this window are flagged for review rather than silently accepted.',
      body: (
        <>
          <Field label="Deployed from">
            <input type="datetime-local" value={from} autoFocus
              onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Recovered at" hint="Leave empty if it is still deployed.">
            <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          {windowBad && <div className="notice bad">Recovery must be after deployment.</div>}
        </>
      ),
      ready: !!from && !windowBad,
    },
    {
      id: 'settings',
      title: 'Depth and gain',
      body: (
        <>
          <Field label="Deploy depth (m)">
            <input type="number" step="0.1" value={depth} autoFocus
              onChange={(e) => setDepth(e.target.value)} />
          </Field>
          <Field
            label="Recording gain"
            hint="As set on the device. Not a dB figure — what a step means depends on board revision and firmware, so levels stay site-relative until the unit is measured on a bench."
          >
            <input list="add-gain" value={gain} onChange={(e) => setGain(e.target.value)}
              placeholder="medium" />
            <datalist id="add-gain">
              {GAIN_STEPS.map((g) => <option key={g} value={g} />)}
            </datalist>
          </Field>
        </>
      ),
      ready: true,
    },
    {
      id: 'purpose',
      title: 'Is this going in the sea?',
      lead: 'A bench or desk test records real audio and is otherwise indistinguishable from a deployment. Only what goes in the water counts towards Ocean impact.',
      body: (
        <div className="stack" style={{ gap: 10 }}>
          <button type="button" className={`pick${purpose === 'deployment' ? ' on' : ''}`}
            onClick={() => setPurpose('deployment')}>
            <strong>In the water</strong>
            <span>A real deployment. Its recordings count towards what Phoniq reports about the ocean.</span>
          </button>
          <button type="button" className={`pick${purpose === 'test' ? ' on' : ''}`}
            onClick={() => setPurpose('test')}>
            <strong>Test or bench</strong>
            <span>Kept and listenable like any other mission, but excluded from Ocean impact.</span>
          </button>
        </div>
      ),
      ready: true,
    },
    {
      id: 'cal',
      title: integrated ? 'Calibration' : 'Do you have a calibration certificate?',
      lead: integrated
        ? undefined
        : 'For this unit specifically. A manufacturer’s typical figure is not one — entering it makes levels look absolute when they are not.',
      body: (
        <>
          {/* Asked only where the answer exists. A sensitivity in dBV re 1 µPa
            * describes a hydrophone handing volts to something else; on a
            * sealed recorder there is no such handover, and the field would be
            * filled from the one number the operator does have — the
            * end-to-end figure — which is a different quantity in a different
            * unit. Better to say so than to accept it. */}
          {integrated ? (
            <div className="notice">
              A <strong>{model.trim() || 'unit'}</strong> is calibrated end to
              end: the element, the gain stage and the converter are one sealed
              part, so there is no point in it where a sensitivity in dBV re
              1 µPa exists. Nothing to enter here, and a figure from the
              datasheet would not be that quantity.
              <br /><br />
              Levels for this deployment stay site-relative, which is the same
              answer a card recorder gets regardless: with no compute there is
              no full-scale voltage to calibrate against. The gain step you
              gave — and the <span className="mono">CONFIG.TXT</span> from the
              card, if there is one — are kept against the mission, so an
              end-to-end figure measured later has something to apply to.
            </div>
          ) : (
            <>
              <Field label="Sensitivity (dBV re 1 µPa)">
                <input type="number" step="0.1" value={sensitivity} autoFocus
                  onChange={(e) => setSensitivity(e.target.value)} placeholder="-173.0" />
              </Field>
              {sensitivity !== '' && (
                <Field label="Certificate reference">
                  <input value={certRef} onChange={(e) => setCertRef(e.target.value)}
                    placeholder="cert-2026-0142 or a URL" />
                </Field>
              )}
            </>
          )}
          <Field label="Notes" hint="Mooring, weather, anything the window does not say.">
            <textarea value={notes} rows={3} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </>
      ),
      ready: true,
    },
  ]

  const steps = kind === 'node' ? nodeSteps : kind === 'recorder' ? recorderSteps : []
  const current = steps[step - 1]
  const last = step === steps.length

  const submit = async () => {
    setBusy(true)
    setErr(null)
    try {
      // Location and position are sent as separate facts, because they are
      // separate facts. `locality_no` identifies the farm and the receiver
      // reads its official name and centre from the register; `lat`/`lon` are
      // where the operator put the pin, and land on the mission rather than on
      // the site. Picking a farm used to send the number alone, which threw
      // the position away and recorded every deployment at the farm's centre.
      const where = {
        ...(place!.locality_no
          ? { locality_no: place!.locality_no }
          : { site: place!.name }),
        ...(place!.lat !== undefined ? { lat: place!.lat } : {}),
        ...(place!.lon !== undefined ? { lon: place!.lon } : {}),
      }
      if (kind === 'node') {
        const res = await commissionNode({
          ...where,
          hydrophone_serial: serial.trim(),
          ...(micType ? { hydrophone_type: micType } : {}),
          ...(sensitivity !== '' ? { sensitivity_dbv_upa: Number(sensitivity) } : {}),
          ...(certRef ? { certificate_ref: certRef } : {}),
          ...(depth !== '' ? { depth_m: Number(depth) } : {}),
          tier,
          purpose,
        })
        setDoneNode(res)
      } else {
        const res = await commissionRecorder({
          ...where,
          serial: serial.trim(),
          recorder_model: model.trim(),
          started_at: asInstant(from),
          ...(to ? { ended_at: asInstant(to), state: 'recovered' } : {}),
          ...(label ? { label } : {}),
          ...(depth !== '' ? { depth_m: Number(depth) } : {}),
          ...(gain ? { gain_setting: gain } : {}),
          // Gated on the model, not on the field being empty: the model step
          // comes first but can be revisited, so a sensitivity typed for one
          // model must not survive being changed to a sealed one.
          ...(sensitivity !== '' && !integrated
            ? { sensitivity_dbv_upa: Number(sensitivity) } : {}),
          ...(certRef && !integrated ? { certificate_ref: certRef } : {}),
          ...(notes ? { notes } : {}),
          purpose,
        } as never)
        setDoneRec(res)
      }
      invalidate('/v1/fleet')
      invalidate('/v1/missions')
      invalidate('/v1/microphones')
      invalidate('/v1/sites')
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail ?? e.code : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  if (doneNode) {
    return (
      <>
        <PageHead title="Ready to deploy" />
        <div className="card" style={{ maxWidth: 620 }}>
          <div className="label">Claim code</div>
          {/* Shown once and never again: only its hash is stored. Large and in
              a font where 0 and O differ — this gets copied onto a box with a
              marker. */}
          <div className="mono" style={{
            fontSize: 40, letterSpacing: '0.16em', margin: '10px 0 20px',
            color: 'var(--signal)', fontFamily: 'var(--mono)',
          }}>{doneNode.claim_code}</div>
          <div className="notice">
            Write this on the node, then power it on at the site. It enrols onto
            the mission below at first boot. The code works once and expires in{' '}
            {doneNode.expires_in_days} days.
          </div>
          <Summary missionId={doneNode.mission_id} siteId={doneNode.site_id}
            unit={doneNode.microphone_id} onAgain={reset} />
        </div>
      </>
    )
  }

  if (doneRec) {
    return (
      <>
        <PageHead title="Recorder registered" />
        <div className="card" style={{ maxWidth: 620 }}>
          {/* No claim code and no empty box where one would go: nothing on a
              recorder can redeem one. */}
          <div className="notice">
            {doneRec.next ?? 'Recover the unit, then import its card from the mission.'}
          </div>
          <Summary missionId={doneRec.mission_id} siteId={doneRec.site_id}
            unit={`${doneRec.microphone_id}${doneRec.recorder_created ? '' : ' — reused, calibration history kept'}`}
            onAgain={reset} />
        </div>
      </>
    )
  }

  function reset() {
    setStep(0); setKind(null); setPlace(null)
    setSerial(''); setModel(''); setLabel(''); setMicType('')
    setSensitivity(''); setCertRef(''); setDepth(''); setGain('')
    setTier('events'); setFrom(''); setTo(''); setNotes(''); setPurpose('deployment')
    setDoneNode(null); setDoneRec(null); setErr(null)
  }

  return (
    <>
      <PageHead title="Add hardware" />

      <div className="card stack" style={{ maxWidth: 620, gap: 18 }}>
        {step > 0 && (
          <ol className="steps" aria-label="Progress">
            {steps.map((s, i) => (
              <li key={s.id} className={i + 1 === step ? 'on' : i + 1 < step ? 'done' : undefined}>
                <span className="sr-only">{s.title}</span>
              </li>
            ))}
          </ol>
        )}

        {step === 0 ? (
          <>
            <h2 className="display" style={{ fontSize: 20, margin: 0 }}>
              What are you adding?
            </h2>
            <p className="lead" style={{ margin: 0 }}>
              The two are registered differently, so this decides everything
              that follows.
            </p>
            <div className="stack" style={{ gap: 10 }}>
              <button className="pick" onClick={() => { setKind('node'); setStep(1) }}>
                <strong>Hydrophone on a node</strong>
                <span>
                  Has an uplink and streams by itself. Ends with a claim code to
                  write on the box.
                </span>
              </button>
              <button className="pick" onClick={() => { setKind('recorder'); setStep(1) }}>
                <strong>Recorder</strong>
                <span>
                  Records to its own memory card with no connection — HydroMoth,
                  SoundTrap, SM4. Recordings arrive when you import the card.
                </span>
              </button>
            </div>
          </>
        ) : current ? (
          <>
            <h2 className="display" style={{ fontSize: 20, margin: 0 }}>{current.title}</h2>
            {current.lead && <p className="lead" style={{ margin: 0 }}>{current.lead}</p>}

            <div className="stack" style={{ gap: 16 }}>{current.body}</div>

            {err && <div className="notice bad">{err}</div>}

            <div className="row" style={{ justifyContent: 'space-between' }}>
              <button className="btn ghost" disabled={busy}
                onClick={() => { setErr(null); setStep(step - 1) }}>
                Back
              </button>
              {last ? (
                <button className="btn primary" disabled={busy || !current.ready}
                  onClick={() => void submit()}>
                  {busy
                    ? 'Saving…'
                    : kind === 'node' ? 'Create claim code' : 'Register recorder'}
                </button>
              ) : (
                <button className="btn primary" disabled={!current.ready}
                  onClick={() => setStep(step + 1)}>
                  Next
                </button>
              )}
            </div>
          </>
        ) : null}
      </div>
    </>
  )
}

function Summary({
  missionId, siteId, unit, onAgain,
}: { missionId: string; siteId: string; unit: string; onAgain: () => void }) {
  return (
    <>
      <dl className="dl" style={{ marginTop: 18 }}>
        <dt>Mission</dt>
        <dd><Link to={`/missions/${encodeURIComponent(missionId)}`}>{missionId}</Link></dd>
        <dt>Site</dt>
        <dd><Link to={`/sites/${encodeURIComponent(siteId)}`}>{siteId}</Link></dd>
        <dt>Unit</dt>
        <dd className="mono">{unit}</dd>
      </dl>
      <div className="row" style={{ marginTop: 20 }}>
        <Link className="btn primary" to="/">Back to fleet</Link>
        <button className="btn ghost" onClick={onAgain}>Add another</button>
      </div>
    </>
  )
}
